<template>
	<div class="consolidate-mappings">
		<div class="header">
			<div class="main-title">Map Branch Data</div>
			<div class="secondary-title">
				To use data from branches you'll need to map them to parameters.<br />If you don't need to use data from branches - you can leave
				this step empty.
			</div>
		</div>
		<div>
			<BranchConsolidateMapping
				v-for="variable in branchVariables"
				:key="variable.id"
				:variable="variable"
				@deleteMapping="deleteMapping"
				@update-path-name="updatePathName"
				:branchRootPaths="branchRootPaths"
				:hasEditPermissions="hasEditPermissions"
				@update-mapping-value="updateMappingValue"
				@update-path-output-is-table="updatePathTableOutput"
				:isLoadingDelete="loadingDeleteVariableIds.includes(variable.id)"
			/>
		</div>
		<div class="add-parameter-link" v-if="hasEditPermissions">
			<AppLoader class="ml-12" v-if="isLoadingAddBranchVariable" />
			<div v-else class="inline-link-style highlighted" @click="addParameter">[+] Add Parameter</div>
		</div>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import BranchConsolidateMapping from './BranchConsolidateMapping';
import { canEditWorkflow } from '@/store/modules/workflows/getters';
import { EdgeTypes } from '@/components/FlowTree/flow-tree-service.const';
import { useNodeIncomingEdges } from '@/store/modules/selectedNode/getters';
import { getNode, useNodeOutgoingEdges } from '@/store/modules/nodes/getters';
import { mapActionsShorthand, mapGettersShorthand } from '@/store/utils/mappers';
import { addBranchVariable, deleteBranchVariable, updateBranchVariable, updateBranchVariableMapping } from '@/store/modules/nodes/actions';
import { editNodeId } from '@/store/modules/selectedNode/getters';
export default {
	name: 'BranchConsolidateMappings',
	components: {
		BranchConsolidateMapping,
	},
	computed: {
		...mapGetters([canEditWorkflow.fullPath]),
		...mapGettersShorthand({ useNodeIncomingEdges, getNode, useNodeOutgoingEdges, editNodeId }),
		hasEditPermissions() {
			return this[canEditWorkflow.fullPath](this.editNode.workflow);
		},
		editNode() {
			return this.getNode(this.editNodeId);
		},
		branchVariables() {
			return this.editNode?.branch_variables;
		},
		branchRootId() {
			return this.consolidatedByEdge?.source;
		},
		consolidatedByEdge() {
			return this.useNodeIncomingEdges.find(({ type }) => type === EdgeTypes.ConsolidatedBy);
		},
		branchRootNode() {
			return this.getNode(this.branchRootId);
		},
		branchRootNodeOutgoingDivergeEdges() {
			const branchRootOutgoingEdges = this.useNodeOutgoingEdges(this.branchRootId);
			return branchRootOutgoingEdges.filter(({ type }) => type === EdgeTypes.Diverge);
		},
		branchRootPaths() {
			// As previous format
			return this.branchRootNodeOutgoingDivergeEdges.map((edge) => ({ id: edge.id, name: edge.branch_path.name }));
		},
	},
	methods: {
		...mapActionsShorthand({ addBranchVariable, deleteBranchVariable, updateBranchVariable, updateBranchVariableMapping }),
		async addParameter() {
			try {
				this.isLoadingAddBranchVariable = true;
				await this.addBranchVariable({ branchRootId: this.branchRootId });
			} catch {
				this.$toast.error('Error while adding parameter, please try again later');
			} finally {
				this.isLoadingAddBranchVariable = false;
			}
		},
		async deleteMapping({ id: branchVariableId }) {
			if (branchVariableId) {
				try {
					this.loadingDeleteVariableIds.push(branchVariableId);
					await this.deleteBranchVariable({ branchRootId: this.branchRootId, branchVariableId });
				} catch {
					this.$toast.error('Error while deleting parameter, please try again later');
				} finally {
					this.loadingDeleteVariableIds = this.loadingDeleteVariableIds.filter((id) => id !== branchVariableId);
				}
			}
		},
		async updatePathName({ id, name }) {
			try {
				await this.updateBranchVariable({ branchRootId: this.branchRootId, branchVariableId: id, partialObjectToUpdate: { name } });
			} catch {
				this.$toast.error('Error while updating parameter name, please try again later');
			}
		},
		async updatePathTableOutput({ id, isTableOutput }) {
			try {
				await this.updateBranchVariable({
					branchVariableId: id,
					branchRootId: this.branchRootId,
					partialObjectToUpdate: { is_table_output: isTableOutput },
				});
			} catch {
				this.$toast.error('Error while updating parameter table output, please try again later');
			}
		},
		async updateMappingValue({ branchId, value, path }) {
			try {
				await this.updateBranchVariableMapping({
					branchId,
					branchPathId: path,
					branchRootId: this.branchRootId,
					partialObjectToUpdate: { value },
				});
			} catch {
				this.$toast.error('Error while updating parameter value, please try again later');
			}
		},
	},
	data() {
		return {
			loadingDeleteVariableIds: [],
			isLoadingAddBranchVariable: false,
		};
	},
};
</script>

<style lang="scss" scoped>
.consolidate-mappings {
	padding: 15px 10px;

	.header {
		margin: 0 0 20px 10px;

		.main-title {
			font-size: 16px;
			font-weight: bold;
		}
		.secondary-title {
			font-size: 12px;
		}
	}
	.add-parameter-link {
		display: inline-block;
		margin: 10px 0 30px 10px;
	}
}
</style>
